import React, { useEffect, useState, useMemo, forwardRef, useRef, useCallback } from 'react';
import { TextField, Autocomplete, Chip, Checkbox, Box, Button, Divider, Typography, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { setFilterKeyword, setFilterLoadedKeyword } from 'src/context/reducers/filter-settings/filterSlice';
import { useLocales } from 'src/locales';
import { FixedSizeList as List } from 'react-window';
import { useLocation } from 'react-router';
import { setSelectedKeywords } from 'src/context/reducers/filter-settings/filterSlice';
import { t } from 'i18next';
import { useTheme } from '@emotion/react';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';

const ListboxComponent = React.memo(
    React.forwardRef<HTMLDivElement, any>((props, ref) => {
        const {
            onToggleKeyword = () => { },
            onOnlyKeyword = () => { },
            allKeywords = [],
            isSearching = false,
            isSelectAllClicked,
            searchValue = '',
            internalSelectedKeywords = [],
            showItemCount = true,
            ...other
        } = props;

        const itemSize = 40;
        const filterStore = useSelector((state: any) => state.filters);
        const listRef = useRef<any>(null);
        const lastScrollOffset = useRef(0);

        const itemData = useMemo(() => {
            const query = searchValue.trim().toLowerCase();

            const selectedItems = internalSelectedKeywords
                .map((kw: any) => {
                    const match = allKeywords.find((i: any) => i.search_term === kw);
                    return {
                        type: 'selected' as const,
                        label: kw,
                        organic: match?.total_organic_searches ?? null,
                    };
                })
                .filter((item: any) => item.label)
                .sort((a: any, b: any) => (b.organic ?? 0) - (a.organic ?? 0));

            const regularItems = allKeywords
                .filter((kw: any) => !internalSelectedKeywords.includes(kw.search_term))
                .filter((kw: any) => !query || kw.search_term.toLowerCase().includes(query))
                .map((kw: any) => ({
                    type: 'regular' as const,
                    label: kw.search_term,
                    organic: kw.total_organic_searches ?? null,
                    data: kw,
                }));

            if (isSearching) {
                return regularItems;
            }

            if (filterStore.keywords.length === 0 && !isSelectAllClicked) {
                return regularItems;
            }

            return [...selectedItems, ...regularItems];
        }, [
            allKeywords,
            internalSelectedKeywords,
            isSearching,
            filterStore.keywords.length,
            isSelectAllClicked,
            searchValue,
        ]);

        return (
            <Box ref={ref} {...other} sx={{ overflow: 'hidden' }}>
                <List
                    ref={listRef}
                    height={350}
                    width="100%"
                    itemSize={itemSize}
                    itemCount={itemData.length}
                    overscanCount={5}
                    onScroll={({ scrollOffset }) => {
                        lastScrollOffset.current = scrollOffset;
                    }}
                    itemKey={(index) => itemData[index].label}
                >
                    {({ index, style }) => {
                        const theme: any = useTheme();
                        const item: any = itemData[index];
                        const isSelected = item.type === 'selected';
                        const selectedBgColor = theme.palette.mode === "dark" ? '#323c47' : '#f6f7f8';

                        return (
                            <Box
                                key={item.label}
                                className="hovered-item"
                                style={style}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 0.6,
                                    position: 'relative',
                                    backgroundColor: isSelected ? selectedBgColor : 'transparent',
                                    borderRadius: isSelected ? '5px' : 'none',
                                    '&:hover': {
                                        backgroundColor: selectedBgColor,
                                        '.hover-only-button': {
                                            display: 'inline-flex',
                                        },
                                        '.organic-count': {
                                            display: 'none',
                                        },
                                        cursor: 'pointer'
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: 0,
                                        flex: 1,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Checkbox
                                        checked={internalSelectedKeywords.includes(item.label)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                        onChange={() => onToggleKeyword(item.label)}
                                    />
                                    <Tooltip
                                        title={item?.label ?? ''}
                                        placement="top"
                                        arrow
                                        PopperProps={{
                                            sx: {
                                                zIndex: 99999,
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: 185,
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Tooltip>
                                </Box>

                                {showItemCount && item.organic != null && (
                                    <Typography
                                        className="organic-count"
                                        sx={{
                                            fontSize: 14,
                                            mr: 1.6,
                                        }}
                                    >
                                        {Math.round(item.organic)}
                                    </Typography>
                                )}

                                <Button
                                    className="hover-only-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOnlyKeyword(item.label);
                                    }}
                                    variant="contained"
                                    color="inherit"
                                    size="small"
                                    sx={{
                                        display: 'none',
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        height: 24,
                                        minWidth: 50,
                                        padding: 0,
                                        justifyContent: 'center',
                                        px: 1,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                                        {t('dashboard.only')}
                                    </Typography>
                                </Button>
                            </Box>
                        );
                    }}
                </List>
            </Box>
        );
    })
);

interface InfiniteScrollKeywordsProps {
    keywords: any[];
    onKeywordChange: (keyword: any) => void;
    resetFilter?: Boolean;
    chipPlacement?: 'header' | 'body';
    selectedKeywords?: string[];
    setSelectedKeywords?: (keywords: string[]) => void;
    placeholderAll?: string;
    width?: number | string;
    showItemCount?: boolean;
}

const InfiniteScrollKeywords: React.FC<InfiniteScrollKeywordsProps> = ({
    keywords,
    onKeywordChange,
    resetFilter,
    selectedKeywords,
    setSelectedKeywords: setSelectedKeywordsProp,
    chipPlacement = 'body',
    placeholderAll,
    showItemCount = true,
    width = 320,
}) => {

    const { t } = useLocales();

    const allPlaceholder = placeholderAll ?? t('dashboard.filters.all_keywords');

    const filterStore = useSelector((state: any) => state.filters);
    const dispatch = useDispatch();
    const location = useLocation();
    const theme: any = useTheme();
    const optionRefs: any = useRef({}); // Référence pour suivre les options survolées
    const [searchValue, setSearchValue] = useState('')
    const [showBoxCounterBox, setShowCounterBox] = useState(false)
    const [showBoxClearBtn, setShowClearBtn] = useState(false)
    const [isSelectAllClicked, setIsSelectAllClicked] = useState(false);
    const [internalSelectedKeywords, setInternalSelectedKeywords] = useState<any[]>([]);
    const [displayChips, setDisplayChips] = useState(false);
    const [prevResetFilter, setPrevResetFilter] = useState(resetFilter);
    const [showAllChip, setShowAllChip] = useState(true);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);
    const [applyPending, setApplyPending] = useState(false);

    const currentKeywords = selectedKeywords ?? filterStore.keywords;

    const updateKeywords = (values: string[]) => {
        if (setSelectedKeywordsProp) {
            setSelectedKeywordsProp(values);
            return;
        }

        dispatch(setFilterKeyword(values));
        dispatch(setSelectedKeywords(values));
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 200); // Attendre 300ms après la dernière frappe

        return () => clearTimeout(timer);
    }, [searchValue]);

    const allKeywords = useMemo(() => filterStore.loadedKeywords, [filterStore.loadedKeywords, location.pathname]);

    const isFirstLoad = useRef(true);

    // ne synchronise l'état local que si Redux a DES vrais keywords
    useEffect(() => {
        setInternalSelectedKeywords(currentKeywords);
        setDisplayChips(currentKeywords.length > 0);
        setShowAllChip(currentKeywords.length === 0);
    }, [currentKeywords]);

    useEffect(() => {
        const ignoreRoutes = [
            '/dash/competitors-monitoring',
            '/dash/competitor-focus',
            '/dash/product-listing-ads',
            '/dash/text-ads'
        ];

        if (!ignoreRoutes.includes(location.pathname) && keywords.length > 0) {
            dispatch(setFilterLoadedKeyword(keywords));
        }
    }, [keywords, dispatch, location.pathname]);

    const pagesWithKeywords = [
        '/dash/competitors-monitoring',
        '/dash/competitor-focus',
        '/dash/product-listing-ads',
        '/dash/text-ads',
        '/dash/business-overview',
        '/dash/traffic-analysis',
        '/dash/brand-wayback',
    ];

    const prevLocation = useRef(location.pathname);



    useEffect(() => {
        const leftKeywordPage = pagesWithKeywords.includes(prevLocation.current);
        const currentIsKeywordPage = pagesWithKeywords.includes(location.pathname);

        // Toujours reset `Select All` et l’état visuel
        setIsSelectAllClicked(false);
        setShowCounterBox(false); // 👈 Ajoute cette ligne
        setInternalSelectedKeywords([]);

        if (leftKeywordPage && !currentIsKeywordPage) {
            dispatch(setSelectedKeywords([]));
            dispatch(setFilterKeyword([]));
            setDisplayChips(false);
            setShowAllChip(true);
            setSearchValue('');
        }

        prevLocation.current = location.pathname;
    }, [location.pathname]);

    const filteredKeywords = useMemo(() => {
        const q = debouncedSearchValue.trim().toLowerCase();
        const isAllMode = currentKeywords.length === 0;

        const source = isAllMode
            ? allKeywords
            : allKeywords.filter((kw: any) => !internalSelectedKeywords.includes(kw.search_term));

        return source.filter((kw: any) => !q || kw.search_term.toLowerCase().includes(q));
    }, [allKeywords, debouncedSearchValue, currentKeywords.length, internalSelectedKeywords]);

    const handleSearchChange = (value: any) => {
        setSearchValue(value)
        setShowCounterBox(value.length > 0)

        if (value.length >= 1) {
            setShowAllChip(false); // 👈 ici on cache la pastille "All"
        }

        if (value.length === 0) {
            setDisplayChips(currentKeywords.length > 0);

            if (currentKeywords.length === 0) {
                setShowAllChip(true);
            }
        }
    }

    const handleClear = () => {
        setSearchValue('');
        setShowClearBtn(false);
        setIsSelectAllClicked(false);
        setInternalSelectedKeywords([]);
        setDisplayChips(false)
        setShowAllChip(true)
        updateKeywords([]);
        setApplyPending(false);
    };

    const handleSelectAll = () => {
        setSearchValue('');
        setDebouncedSearchValue('');

        const filteredTerms = filteredKeywords.map((kw: any) => kw.search_term);
        const isSelectingAllVisible = filteredTerms.every((term: any) =>
            internalSelectedKeywords.includes(term)
        );

        if (isSelectingAllVisible) {
            setInternalSelectedKeywords([]);
            setIsSelectAllClicked(false);
            setShowCounterBox(false);
            setShowAllChip(true);
            return;
        }

        const merged = Array.from(new Set([...internalSelectedKeywords, ...filteredTerms]));

        setInternalSelectedKeywords(merged);
        setIsSelectAllClicked(true);
        setApplyPending(true);
        setShowCounterBox(true);
        setShowClearBtn(true);
    };

    const handleApply = () => {
        if (internalSelectedKeywords.length === allKeywords.length) {
            updateKeywords([]);
            setInternalSelectedKeywords([]);
            setDisplayChips(false);
            setShowAllChip(true);
        } else {
            updateKeywords(internalSelectedKeywords);
            setDisplayChips(internalSelectedKeywords.length > 0);
        }

        setIsSelectAllClicked(false);
        setApplyPending(false);
        setSearchValue('');
        setShowCounterBox(false);
        inputRef.current?.blur();
    };

    const handleKeywordChange = (keyword: string) => {
        const updated = internalSelectedKeywords.includes(keyword)
            ? internalSelectedKeywords.filter((kw) => kw !== keyword)
            : [...internalSelectedKeywords, keyword];

        // 👉 Mode Select All → mise à jour locale uniquement
        if (applyPending || (currentKeywords.length === 0 && isSelectAllClicked)) {
            setInternalSelectedKeywords(updated);
            setDisplayChips(updated.length > 0);
            return;
        }

        // 👉 Mode normal
        if (updated.length === allKeywords.length) {
            updateKeywords([]);
            setInternalSelectedKeywords([]);
            setDisplayChips(false);
            setShowAllChip(true);
            return;
        }

        updateKeywords(updated);
        setInternalSelectedKeywords(updated);
        setDisplayChips(updated.length > 0);
    };



    const handleOnlyKeyword = (keyword: string) => {
        const singleKeyword = [keyword];
        updateKeywords(singleKeyword);
        setInternalSelectedKeywords(singleKeyword);
        setDisplayChips(true);
        setShowAllChip(false);
    };

    const autocompletevalue = useMemo(() => {
        return currentKeywords.length === 0
            ? [{ search_term: allPlaceholder, total_organic_searches: null }]
            : allKeywords.filter(
                (option: any) =>
                    option?.search_term &&
                    currentKeywords.includes(option.search_term)
            );
    }, [currentKeywords, allKeywords, allPlaceholder]);


    const handleAutocompleteChange = (event: any, value: any) => {
        const newValues = value.map((option: any) => option.search_term);

        const updatedSet = new Set([...internalSelectedKeywords, ...newValues]);
        const updatedValues = Array.from(updatedSet);

        setInternalSelectedKeywords(updatedValues);

        if (updatedValues.length === 0) {
            handleClear();
        }
    };

    const matchingKeywordsCount = useMemo(() => {
        if (searchValue.trim()) {
            return filteredKeywords.length;
        }
        return internalSelectedKeywords.length;
    }, [filteredKeywords, internalSelectedKeywords, searchValue]);


    useEffect(() => {
        //     // Only clear if resetFilter changes from false to true
        if (resetFilter && !prevResetFilter) {
            setShowAllChip(true)
            handleClear();
        }
        // Update previous value
        setPrevResetFilter(resetFilter);
    }, [resetFilter]);

    useEffect(() => {
        setShowAllChip(currentKeywords.length === 0);
    }, [currentKeywords.length]);

    const isSearching = searchValue.trim().length > 0;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const memoizedSelectedKeywords = useMemo(
        () => currentKeywords,
        [currentKeywords]
    );

    const memoizedAllKeywords = useMemo(() => allKeywords, [allKeywords]);

    const handleKeywordChangeMemo = useCallback((kw: string) => {
        handleKeywordChange(kw);
    }, [internalSelectedKeywords, allKeywords]);

    const chipTextColor =
        chipPlacement === 'header' ? theme.palette.common.white : theme.palette.text.primary;

    return (
        <Box sx={{
            width: {
                lg: width, xs: '100%',
            }
        }}>
            <Autocomplete
                multiple
                disableClearable={currentKeywords.length === 0}
                inputValue={searchValue}
                onInputChange={(event, newValue, reason) => {
                    if (reason === 'clear') {
                        handleClear(); // réinitialisation complète
                    } else {
                        handleSearchChange(newValue);
                    }
                }}
                size="small"
                disableCloseOnSelect
                options={filteredKeywords}
                getOptionLabel={(option: any) => option.search_term}
                value={autocompletevalue}
                onChange={(e, newValue, reason) => {
                    if (reason === 'clear') {
                        handleClear();
                    } else {
                        handleAutocompleteChange(e, newValue);
                    }
                }} isOptionEqualToValue={(option, value) => option.search_term === value.search_term}
                renderInput={(params: any) => (
                    <TextField
                        {...params}
                        inputRef={(el) => {
                            inputRef.current = el;
                            if (typeof params.inputRef === 'function') {
                                params.inputRef(el);
                            } else if (params.inputRef) {
                                (params.inputRef as React.MutableRefObject<any>).current = el;
                            }
                        }}
                    />
                )}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: '36px',
                        minHeight: '36px',
                        flexWrap: 'nowrap',
                        overflow: 'hidden',
                    },

                    '& .MuiAutocomplete-tag': {
                        flexShrink: 0,
                    },

                    '& .MuiAutocomplete-input': {
                        minWidth: '0 !important',
                        width: '0 !important',
                    },
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: '320px',
                            overflowX: 'hidden',
                        },
                    },
                }}
                ListboxComponent={(props) => (
                    <div style={{ position: 'relative' }}>
                        {showBoxCounterBox && (
                            <Box sx={{ mt: 1, px: 2 }}>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography style={{ fontSize: "14px" }}>
                                        {matchingKeywordsCount} keywords {isSelectAllClicked ? t('dashboard.selected') : t('dashboard.textAdsPage.found')}
                                    </Typography>

                                    {
                                        isSelectAllClicked ? (
                                            <Button
                                                onClick={handleApply}
                                                onMouseDown={(event) => event.preventDefault()}

                                                variant="contained"
                                                color="inherit"
                                                size="small"
                                                sx={{ height: 24, minWidth: 50, px: 1 }}
                                            >
                                                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{t('dashboard.apply')}</Typography>
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={handleSelectAll}
                                                onMouseDown={(event) => event.preventDefault()}
                                                variant="contained"
                                                color="inherit"
                                                size="small"
                                                sx={{ height: 24, minWidth: 50, px: 1, whiteSpace: 'nowrap' }}
                                            >
                                                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{t('dashboard.selectAll')}</Typography>
                                            </Button>
                                        )
                                    }
                                </Box>
                                <Divider sx={{ my: 1 }} />
                            </Box>
                        )}
                        <ListboxComponent
                            {...props}
                            showItemCount={showItemCount}
                            isSearching={isSearching}
                            searchValue={searchValue}
                            style={{ overflow: 'hidden' }}
                            selectedKeywords={memoizedSelectedKeywords}
                            onToggleKeyword={handleKeywordChangeMemo}
                            ListboxProps={{
                                style: {
                                    overflow: 'hidden',
                                    padding: 0,
                                    margin: 0,
                                    maxHeight: 'unset',
                                },
                            }}
                            onOnlyKeyword={handleOnlyKeyword}
                            allKeywords={memoizedAllKeywords}
                            isSelectAllClicked={isSelectAllClicked}
                            internalSelectedKeywords={internalSelectedKeywords}
                        />

                    </div>
                )}
                onClose={(_: any, reason: string) => {
                    // si on ferme sans avoir cliqué sur “Apply”
                    if (
                        reason === 'blur' || reason === 'escape' || reason === 'toggleInput'
                    ) {
                        // et qu’on n’est pas en “Select All” ni en mode filtré Redux
                        if (!isSelectAllClicked && currentKeywords.length === 0) {
                            // on remet le champ vide et on restaure le chip “All”
                            setSearchValue('');
                            setShowCounterBox(false);
                            setDisplayChips(false);
                            setShowAllChip(true);
                        }
                    }
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.search_term}
                        ref={(el) => (optionRefs.current[option.search_term] = el)}
                        onClick={() =>
                            handleKeywordChange(option.search_term)
                        }
                        onMouseEnter={() => {

                            optionRefs.current[option.search_term].classList.add('hovered');
                        }}
                        onMouseLeave={() => {
                            optionRefs.current[option.search_term].classList.remove('hovered');
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                {location.pathname !== "/dash/brand-wayback" &&
                                    <Checkbox
                                        checked={
                                            currentKeywords.length === 0 && !isSelectAllClicked
                                                ? false
                                                : internalSelectedKeywords.includes(option.search_term)
                                        }
                                        onChange={() => handleKeywordChange(option.search_term)}
                                        onClick={(event) => event.stopPropagation()} // Empêcher le clic de la checkbox de déclencher le clic de l'option
                                    />
                                }
                                <Box
                                    sx={{
                                        width: 'auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flexGrow: 1,
                                    }}
                                >
                                    {option?.search_term}
                                </Box>
                            </div>
                            {location.pathname !== "/dash/brand-wayback" &&
                                <Button
                                    className="hover-only-button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleOnlyKeyword(option.search_term);
                                    }}
                                    variant="contained"
                                    color="inherit"
                                    size="small"
                                    sx={{
                                        mt: 0.3,
                                        height: 24,
                                        minWidth: 50,
                                        padding: 0,
                                        display: 'none',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {t('dashboard.only')}
                                </Button>
                            }
                            {!["/dash/product-listing-ads", "/dash/text-ads", "/dash/brand-wayback"].includes(location.pathname) && (
                                <div className={location.pathname !== "/dash/brand-wayback" ? "organic-searches" : ""} style={{ paddingRight: 10 }}>
                                    {option.search_term !== 'All' && (option?.total_organic_searches ?? 0).toFixed(0)}
                                </div>
                            )}
                        </div>

                    </li>
                )}
                renderTags={(value, getTagProps) => {
                    const firstLabel: any = value[0]?.search_term || value[0];
                    const remainingCount = value.length - 1;

                    const props: any = {}

                    if (currentKeywords.length >= 1) {
                        Object.assign(props, getTagProps({ index: 0 }))

                        props.onDelete = () => {
                            const updated = currentKeywords.filter((kw: any) => kw !== firstLabel);
                            updateKeywords(updated);
                            setInternalSelectedKeywords(updated);
                        }
                    }

                    return [
                        <Chip
                            key="first"
                            variant="soft"
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 140,
                                backgroundColor: 'rgba(145, 158, 171, 0.12)',

                                '& .MuiChip-label': {
                                    color: '#333C47',
                                    fontFamily: '"Public Sans"',
                                    fontSize: '13px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '23px',
                                    px: 1,
                                },

                                '& .MuiChip-deleteIcon': {
                                    color: chipTextColor,
                                },
                            }}
                            label={
                                firstLabel.length > 16 ? `${firstLabel.slice(0, 16)}...` : firstLabel
                            }
                            size="small"
                            {...props}
                        />,
                        remainingCount > 0 && (
                            <Chip
                                key="more"
                                label={`+${remainingCount}`}
                                size="small"
                                variant="soft"
                                sx={{
                                    color: chipTextColor,
                                    ml: 0.3,
                                }}
                            />
                        ),
                    ];
                }}
            />
        </Box>
    );
};

export default InfiniteScrollKeywords;
